import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ordersAPI, paymentAPI, reviewsAPI, usersAPI, listingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatCurrency, formatDate, formatDateTime, getInitials, getResponsiveThumbnailUrl } from '@/lib/utils';
import {
    CreditCard,
    Package,
    Truck,
    CheckCircle,
    Clock,
    AlertTriangle,
    ShieldCheck,
    Loader2,
    ArrowLeft,
    RefreshCw,
    MapPin,
    User,
    FileText,
    XCircle,
    Star,
    Trash2,
    MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const OrderPage = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [pollingStatus, setPollingStatus] = useState(false);
    
    // Review state
    const [reviewDialog, setReviewDialog] = useState(false);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);
    const [canReview, setCanReview] = useState(false);
    const [hasReviewed, setHasReviewed] = useState(false);
    
    // Shipping state
    const [shippingDialog, setShippingDialog] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [courierCompany, setCourierCompany] = useState('');
    const [confirmingShipment, setConfirmingShipment] = useState(false);
    
    // Courier companies list
    const courierCompanies = [
        { value: 'fan_courier', label: 'Fan Courier', trackingUrl: 'https://www.fancourier.ro/awb-tracking/?tracking=' },
        { value: 'cargus', label: 'Cargus', trackingUrl: 'https://www.cargus.ro/tracking/?t=' },
        { value: 'dpd', label: 'DPD', trackingUrl: 'https://tracking.dpd.ro/?reference=' },
        { value: 'sameday', label: 'Sameday', trackingUrl: 'https://sameday.ro/#awb=' },
        { value: 'gls', label: 'GLS', trackingUrl: 'https://gls-group.eu/RO/ro/urmarire-colete?match=' },
        { value: 'posta_romana', label: 'Poșta Română', trackingUrl: 'https://www.posta-romana.ro/track-trace.html?id=' },
        { value: 'urgent_cargus', label: 'Urgent Cargus', trackingUrl: 'https://www.urgentcargus.ro/tracking.aspx?t=' },
        { value: 'nemo', label: 'Nemo Express', trackingUrl: 'https://nemoexpress.ro/tracking/?awb=' },
        { value: 'other', label: 'Altul', trackingUrl: 'https://parcelsapp.com/en/tracking/' },
    ];
    
    const getTrackingUrl = (courier, awb) => {
        const company = courierCompanies.find(c => c.value === courier);
        if (company) {
            return company.trackingUrl + awb;
        }
        // Default to universal tracking service
        return `https://parcelsapp.com/en/tracking/${awb}`;
    };
    
    const getCourierLabel = (courierValue) => {
        const company = courierCompanies.find(c => c.value === courierValue);
        return company ? company.label : courierValue;
    };
    
    // Dispute state
    const [disputeDialog, setDisputeDialog] = useState(false);
    const [disputeReason, setDisputeReason] = useState('');
    const [submittingDispute, setSubmittingDispute] = useState(false);
    
    // Participant info state (seller/buyer profile)
    const [sellerInfo, setSellerInfo] = useState(null);
    const [buyerInfo, setBuyerInfo] = useState(null);
    const [participantInfo, setParticipantInfo] = useState(null);
    const [otherParticipantInfo, setOtherParticipantInfo] = useState(null);
    
    // Delete listing state
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deletingListing, setDeletingListing] = useState(false);

    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');

    const fetchOrder = useCallback(async () => {
        try {
            const res = await ordersAPI.getById(orderId);
            setOrder(res.data);
            
            // Check if user can review
            if (res.data.order_status === 'completed') {
                try {
                    const reviewRes = await reviewsAPI.getByOrder(orderId);
                    setCanReview(reviewRes.data.can_review);
                    setHasReviewed(reviewRes.data.has_reviewed);
                } catch (e) {
                    // If endpoint fails, assume can review if completed
                    setCanReview(true);
                    setHasReviewed(false);
                }
            }
            
            // Fetch participant info (seller for buyer view, buyer for seller view)
            const isBuyer = user?.user_id === res.data.buyer_id;
            const participantId = isBuyer ? res.data.seller_id : res.data.buyer_id;
            const otherParticipantId = isBuyer ? res.data.buyer_id : res.data.seller_id;
            
            // Always fetch both seller and buyer info
            try {
                const sellerStatsRes = await usersAPI.getStats(res.data.seller_id);
                setSellerInfo(sellerStatsRes.data);
                // Set participantInfo for backwards compatibility
                if (isBuyer) setParticipantInfo(sellerStatsRes.data);
            } catch (e) {
                console.error('Error fetching seller info:', e);
            }
            
            try {
                const buyerStatsRes = await usersAPI.getStats(res.data.buyer_id);
                setBuyerInfo(buyerStatsRes.data);
                // Set participantInfo for backwards compatibility
                if (!isBuyer) setParticipantInfo(buyerStatsRes.data);
            } catch (e) {
                console.error('Error fetching buyer info:', e);
            }
            
            // For completed orders, also fetch the other participant (so seller can see buyer info)
            if (res.data.order_status === 'completed') {
                try {
                    const otherStatsRes = await usersAPI.getStats(otherParticipantId);
                    setOtherParticipantInfo(otherStatsRes.data);
                } catch (e) {
                    console.error('Error fetching other participant info:', e);
                }
            }
            
            return res.data;
        } catch (error) {
            console.error('Error fetching order:', error);
            toast.error('Eroare la încărcarea comenzii');
            return null;
        } finally {
            setLoading(false);
        }
    }, [orderId, user?.user_id]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    // Poll payment status if returning from Stripe
    useEffect(() => {
        if (sessionId && order && order.order_status === 'pending_payment') {
            const pollPaymentStatus = async (attempts = 0) => {
                const maxAttempts = 10;
                const pollInterval = 2000;

                if (attempts >= maxAttempts) {
                    toast.error('Verificarea plății a expirat. Reîncarcă pagina.');
                    setPollingStatus(false);
                    return;
                }

                setPollingStatus(true);
                try {
                    const res = await paymentAPI.getStatus(sessionId);
                    
                    if (res.data.payment_status === 'paid') {
                        toast.success('Plată procesată cu succes!');
                        setPollingStatus(false);
                        // Remove session_id from URL and refresh order
                        navigate(`/orders/${orderId}`, { replace: true });
                        await fetchOrder();
                        return;
                    } else if (res.data.status === 'expired') {
                        toast.error('Sesiunea de plată a expirat.');
                        setPollingStatus(false);
                        return;
                    }

                    // Continue polling
                    setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
                } catch (error) {
                    console.error('Error checking payment:', error);
                    setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
                }
            };

            pollPaymentStatus();
        }
    }, [sessionId, order, orderId, navigate, fetchOrder]);

    const handlePayment = async () => {
        setProcessingPayment(true);
        try {
            // MOCK: Confirm payment directly without going to Stripe
            await ordersAPI.mockPayment(orderId);
            toast.success('Plată confirmată cu succes! (MOCK)');
            await fetchOrder();
        } catch (error) {
            // Fallback to Stripe if mock endpoint doesn't exist
            try {
                const originUrl = window.location.origin;
                const res = await paymentAPI.createCheckout(orderId, originUrl);
                if (res.data.checkout_url) {
                    window.location.href = res.data.checkout_url;
                }
            } catch (stripeError) {
                toast.error(stripeError.response?.data?.detail || 'Eroare la inițializarea plății');
            }
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleConfirmDelivery = async () => {
        try {
            await ordersAPI.confirmDelivery(orderId);
            toast.success('Livrare confirmată! Fondurile au fost eliberate vânzătorului.');
            fetchOrder();
        } catch (error) {
            toast.error('Eroare la confirmarea livrării');
        }
    };

    const handleConfirmShipment = async () => {
        if (!trackingNumber.trim()) {
            toast.error('Introdu numărul de urmărire AWB');
            return;
        }
        if (!courierCompany) {
            toast.error('Selectează firma de curierat');
            return;
        }
        
        setConfirmingShipment(true);
        try {
            await ordersAPI.ship(orderId, trackingNumber.trim(), courierCompany);
            toast.success('Expediere confirmată!');
            setShippingDialog(false);
            setTrackingNumber('');
            setCourierCompany('');
            fetchOrder();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la confirmarea expedierii');
        } finally {
            setConfirmingShipment(false);
        }
    };

    const handleSubmitDispute = async () => {
        if (!disputeReason.trim() || disputeReason.trim().length < 20) {
            toast.error('Descrierea disputei trebuie să aibă minim 20 caractere');
            return;
        }
        
        setSubmittingDispute(true);
        try {
            await ordersAPI.createDispute(orderId, disputeReason.trim());
            toast.success('Disputa a fost deschisă. Echipa noastră o va analiza.');
            setDisputeDialog(false);
            setDisputeReason('');
            fetchOrder();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la deschiderea disputei');
        } finally {
            setSubmittingDispute(false);
        }
    };

    const handleCloseDispute = async () => {
        try {
            await ordersAPI.closeDispute(orderId);
            toast.success('Disputa a fost închisă.');
            fetchOrder();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la închiderea disputei');
        }
    };

    const handleSubmitReview = async () => {
        setSubmittingReview(true);
        try {
            await reviewsAPI.create({
                order_id: orderId,
                rating: reviewRating,
                comment: reviewComment.trim() || null,
            });
            toast.success('Recenzia a fost adăugată cu succes!');
            setReviewDialog(false);
            setHasReviewed(true);
            setCanReview(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la adăugarea recenziei');
        } finally {
            setSubmittingReview(false);
        }
    };

    const handleDeleteListing = async () => {
        setDeletingListing(true);
        try {
            await listingsAPI.delete(order.listing_id);
            toast.success('Anunțul a fost șters cu succes!');
            setDeleteDialog(false);
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea anunțului');
        } finally {
            setDeletingListing(false);
        }
    };

    const getStatusInfo = (status) => {
        const statuses = {
            'pending_payment': { label: 'Așteaptă plata', color: 'yellow', icon: Clock },
            'paid': { label: 'Plătit', color: 'blue', icon: CreditCard },
            'shipped': { label: 'Expediat', color: 'purple', icon: Truck },
            'delivered': { label: 'Livrat', color: 'green', icon: Package },
            'completed': { label: 'Finalizat', color: 'green', icon: CheckCircle },
            'cancelled': { label: 'Anulat', color: 'red', icon: AlertTriangle },
            'disputed': { label: 'În dispută', color: 'orange', icon: AlertTriangle },
        };
        return statuses[status] || { label: status, color: 'gray', icon: Package };
    };

    const getEscrowInfo = (status) => {
        const statuses = {
            'pending': { label: 'În așteptare', color: 'gray' },
            'held': { label: 'Securizat', color: 'blue' },
            'released': { label: 'Eliberat', color: 'green' },
            'refunded': { label: 'Rambursat', color: 'orange' },
            'disputed': { label: 'În dispută', color: 'red' },
        };
        return statuses[status] || { label: status, color: 'gray' };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                    <Skeleton className="h-8 w-48 mb-6" />
                    <div className="grid gap-6">
                        <Skeleton className="h-64" />
                        <Skeleton className="h-48" />
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-bold mb-2">Comandă negăsită</h2>
                    <p className="text-muted-foreground mb-4">Această comandă nu există</p>
                    <Button onClick={() => navigate('/dashboard')} className="rounded-full">
                        Înapoi la portal
                    </Button>
                </div>
            </div>
        );
    }

    const statusInfo = getStatusInfo(order.order_status);
    const escrowInfo = getEscrowInfo(order.escrow_status);
    const StatusIcon = statusInfo.icon;
    const isBuyer = user?.user_id === order.buyer_id;
    const isSeller = user?.user_id === order.seller_id;

    return (
        <div className="min-h-screen bg-background py-8" data-testid="order-page">
            <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                {/* Back button */}
                <Button
                    variant="ghost"
                    className="mb-6 rounded-full"
                    onClick={() => navigate('/dashboard')}
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Înapoi la portal
                </Button>

                {/* Order Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-heading text-2xl font-bold">Comandă #{order.order_id.slice(-8)}</h1>
                        <p className="text-muted-foreground">Creată la {formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge className={`bg-${statusInfo.color}-100 text-${statusInfo.color}-700 px-3 py-1`}>
                            <StatusIcon className="w-4 h-4 mr-1" />
                            {statusInfo.label}
                        </Badge>
                    </div>
                </div>

                {/* Polling indicator */}
                {pollingStatus && (
                    <Card className="mb-6 border-blue-200 bg-blue-50">
                        <CardContent className="py-4">
                            <div className="flex items-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="text-blue-700">Se verifică statusul plății...</span>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Product Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="w-5 h-5" />
                                    Produs
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div 
                                    className="flex gap-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                                    onClick={() => navigate(`/listing/${order.listing_slug || order.listing_id}`)}
                                >
                                    {(order.listing_thumbnail || order.listing_image) ? (
                                        <img
                                            src={getResponsiveThumbnailUrl(order.listing_thumbnail || order.listing_image, 200, 100, 75)}
                                            alt={order.listing_title}
                                            className="w-24 h-24 object-cover rounded-lg shrink-0"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                                            <Package className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-lg">{order.listing_title}</h3>
                                        <p className="text-2xl font-bold text-primary mt-2">
                                            {formatCurrency(order.amount)}
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Delete listing button - only for seller when order is cancelled or completed */}
                                {isSeller && ['cancelled', 'completed'].includes(order.order_status) && (
                                    <Button
                                        variant="outline"
                                        className="w-full mt-4 rounded-full text-red-600 border-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteDialog(true);
                                        }}
                                        data-testid="delete-listing-btn"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Șterge anunțul
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {/* Shipping Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5" />
                                    Livrare
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Adresa de livrare</p>
                                    <p className="font-medium">{order.shipping_address}</p>
                                </div>
                                
                                {order.tracking_number && (
                                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm font-medium text-foreground">Tracking Colet</p>
                                            {order.courier_company && (
                                                <Badge variant="outline" className="text-primary border-primary/30">
                                                    {getCourierLabel(order.courier_company)}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="font-mono font-bold text-lg text-foreground mb-3">{order.tracking_number}</p>
                                        <a 
                                            href={getTrackingUrl(order.courier_company, order.tracking_number)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-full text-sm font-medium transition-colors"
                                        >
                                            <Truck className="w-4 h-4" />
                                            Urmărește coletul
                                        </a>
                                    </div>
                                )}

                                {/* Confirm shipment button for seller */}
                                {isSeller && order.order_status === 'paid' && (
                                    <Button
                                        className="w-full rounded-full bg-primary hover:bg-primary/90"
                                        onClick={() => setShippingDialog(true)}
                                        data-testid="confirm-shipment-btn"
                                    >
                                        <Truck className="w-4 h-4 mr-2" />
                                        Confirmă expedierea
                                    </Button>
                                )}

                                {/* Confirm delivery button for buyer */}
                                {isBuyer && order.order_status === 'shipped' && (
                                    <Button
                                        className="w-full rounded-full bg-green-600 hover:bg-green-700"
                                        onClick={handleConfirmDelivery}
                                        data-testid="confirm-delivery-btn"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Confirmă primirea
                                    </Button>
                                )}

                                {/* Open dispute button - only for paid/shipped orders, NOT delivered */}
                                {(isBuyer || isSeller) && ['paid', 'shipped'].includes(order.order_status) && !order.dispute_id && (
                                    <Button
                                        variant="outline"
                                        className="w-full rounded-full text-orange-600 border-orange-600 hover:bg-orange-50"
                                        onClick={() => setDisputeDialog(true)}
                                        data-testid="open-dispute-btn"
                                    >
                                        <AlertTriangle className="w-4 h-4 mr-2" />
                                        Deschide dispută
                                    </Button>
                                )}

                                {/* Leave review button - HIDDEN
                                {isBuyer && order.order_status === 'completed' && !hasReviewed && (
                                    <Button
                                        className="w-full rounded-full bg-yellow-500 hover:bg-yellow-600"
                                        onClick={() => setReviewDialog(true)}
                                        data-testid="leave-review-completed-btn"
                                    >
                                        <Star className="w-4 h-4 mr-2" />
                                        Lasă recenzie
                                    </Button>
                                )}
                                */}

                                {order.dispute_id && order.order_status === 'disputed' && (
                                    <div className="space-y-2">
                                        <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
                                            <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                Dispută deschisă - în curs de analiză
                                            </p>
                                        </div>
                                        {/* Close dispute button - only for opener */}
                                        {order.dispute_opened_by === user?.user_id && (
                                            <Button
                                                className="w-full rounded-full"
                                                onClick={handleCloseDispute}
                                            >
                                                <XCircle className="w-4 h-4 mr-2" />
                                                Închide disputa
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Order Timeline */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    Istoricul comenzii
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Comandă creată</p>
                                            <p className="text-sm text-muted-foreground">{formatDateTime(order.created_at)}</p>
                                        </div>
                                    </div>

                                    {order.order_status !== 'pending_payment' && order.order_status !== 'cancelled' && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                                <CreditCard className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Plată primită</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {order.paid_at ? formatDateTime(order.paid_at) : 'Fonduri securizate'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {['shipped', 'delivered', 'completed'].includes(order.order_status) && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                <Truck className="w-4 h-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Expediat</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {order.shipped_at ? formatDateTime(order.shipped_at) : (order.tracking_number ? `Tracking: ${order.tracking_number}` : '')}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {['delivered', 'completed'].includes(order.order_status) && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                                <Package className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Livrat</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {order.delivered_at ? formatDateTime(order.delivered_at) : 'Confirmat de cumpărător'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {order.order_status === 'completed' && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Finalizat</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {order.completed_at ? formatDateTime(order.completed_at) : 'Fonduri eliberate vânzătorului'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {order.order_status === 'cancelled' && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                                <XCircle className="w-4 h-4 text-red-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Anulat</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {order.cancelled_at ? formatDateTime(order.cancelled_at) : 'Comandă anulată'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Payment Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CreditCard className="w-5 h-5" />
                                    Plată
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Preț produs</span>
                                        <span>{formatCurrency(order.product_amount || (order.amount - (order.shipping_cost || 0)))}</span>
                                    </div>
                                    {order.shipping_cost > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Transport</span>
                                            <span>{formatCurrency(order.shipping_cost)}</span>
                                        </div>
                                    )}
                                {/* Platform fee - TEMPORARILY HIDDEN
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Taxă platformă (10%)</span>
                                    <span>{formatCurrency(order.platform_fee)}</span>
                                </div>
                                */}
                                    <Separator />
                                    <div className="flex justify-between font-bold">
                                        <span>Total</span>
                                        <span className="text-primary">{formatCurrency(order.amount)}</span>
                                    </div>
                                </div>

                                {/* Payment button for buyer */}
                                {isBuyer && order.order_status === 'pending_payment' && (
                                    <Button
                                        className="w-full rounded-full bg-primary hover:bg-primary/90"
                                        onClick={handlePayment}
                                        disabled={processingPayment}
                                        data-testid="pay-now-btn"
                                    >
                                        {processingPayment ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Se procesează...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="w-4 h-4 mr-2" />
                                                Am plătit
                                            </>
                                        )}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {/* Payment Protection Card - TEMPORARILY HIDDEN
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5" />
                                    Protecție Plată
                                </CardTitle>
                                <CardDescription>
                                    Protecție pentru cumpărător și vânzător
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Status fonduri</span>
                                    <Badge className={
                                        order.escrow_status === 'held' ? 'bg-blue-100 text-blue-700' :
                                        order.escrow_status === 'released' ? 'bg-green-100 text-green-700' :
                                        order.escrow_status === 'refunded' ? 'bg-gray-100 text-gray-700' :
                                        order.escrow_status === 'disputed' ? 'bg-orange-100 text-orange-700' :
                                        'bg-gray-100 text-gray-700'
                                    }>
                                        {escrowInfo.label}
                                    </Badge>
                                </div>
                                
                                <div className={`mt-4 p-3 rounded-lg ${
                                    order.escrow_status === 'disputed' ? 'bg-orange-50' : 'bg-blue-50'
                                }`}>
                                    <p className={`text-sm ${
                                        order.escrow_status === 'disputed' ? 'text-orange-700' : 'text-blue-700'
                                    }`}>
                                        {order.escrow_status === 'pending' && 
                                            'Banii vor fi reținuți în siguranță după plată.'}
                                        {order.escrow_status === 'held' && 
                                            'Banii sunt reținuți în siguranță până la confirmarea livrării.'}
                                        {order.escrow_status === 'released' && 
                                            'Banii au fost eliberați către vânzător.'}
                                        {order.escrow_status === 'refunded' && 
                                            'Banii au fost rambursați către cumpărător.'}
                                        {order.escrow_status === 'disputed' && 
                                            'Fonduri blocate - dispută în curs de analiză.'}
                                    </p>
                                </div>

                                {isSeller && order.amount && (
                                    <div className="mt-4 pt-4 border-t">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Vei primi</span>
                                            <span className="font-bold text-green-600">
                                                {formatCurrency(order.seller_amount)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        */}

                        {/* Seller Info Card */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <User className="w-4 h-4" />
                                    Vânzător
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-4">
                                    <Link to={`/user/${order.seller_username}`}>
                                        <Avatar className="w-12 h-12 border-2 border-border cursor-pointer hover:border-primary transition-colors">
                                            <AvatarImage 
                                                src={sellerInfo?.profile_picture_url || sellerInfo?.picture} 
                                                alt={order.seller_username}
                                            />
                                            <AvatarFallback className="text-sm bg-primary/10">
                                                {getInitials(sellerInfo?.name || order.seller_username)}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <Link 
                                            to={`/user/${order.seller_username}`}
                                            className="font-medium hover:text-primary transition-colors block truncate"
                                        >
                                            {sellerInfo?.name || order.seller_username}
                                        </Link>
                                        <span className="text-xs text-muted-foreground">@{order.seller_username}</span>
                                        {sellerInfo && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                                <span className="text-xs font-medium">
                                                    {sellerInfo.average_rating?.toFixed(1) || 'N/A'}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    ({sellerInfo.total_reviews || 0})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {user?.user_id !== order.seller_id && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/messages?recipient=${order.seller_id}&name=${sellerInfo?.name || order.seller_username}`)}
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Buyer Info Card */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <User className="w-4 h-4" />
                                    Cumpărător
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-4">
                                    <Link to={`/user/${order.buyer_username}`}>
                                        <Avatar className="w-12 h-12 border-2 border-border cursor-pointer hover:border-primary transition-colors">
                                            <AvatarImage 
                                                src={buyerInfo?.profile_picture_url || buyerInfo?.picture} 
                                                alt={order.buyer_username}
                                            />
                                            <AvatarFallback className="text-sm bg-primary/10">
                                                {getInitials(buyerInfo?.name || order.buyer_username)}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <Link 
                                            to={`/user/${order.buyer_username}`}
                                            className="font-medium hover:text-primary transition-colors block truncate"
                                        >
                                            {buyerInfo?.name || order.buyer_username}
                                        </Link>
                                        <span className="text-xs text-muted-foreground">@{order.buyer_username}</span>
                                        {buyerInfo && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                                <span className="text-xs font-medium">
                                                    {buyerInfo.average_rating?.toFixed(1) || 'N/A'}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    ({buyerInfo.total_reviews || 0})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {user?.user_id !== order.buyer_id && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/messages?recipient=${order.buyer_id}&name=${buyerInfo?.name || order.buyer_username}`)}
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Review Card - only for completed orders */}
                        {order.order_status === 'completed' && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Star className="w-5 h-5" />
                                        Recenzie
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {hasReviewed ? (
                                        <div className="text-center py-4">
                                            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                                            <p className="text-muted-foreground">Ai lăsat deja o recenzie</p>
                                        </div>
                                    ) : canReview ? (
                                        <Button
                                            className="w-full rounded-full"
                                            onClick={() => setReviewDialog(true)}
                                            data-testid="leave-review-btn"
                                        >
                                            <Star className="w-4 h-4 mr-2" />
                                            Lasă o recenzie
                                        </Button>
                                    ) : null}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            {/* Review Dialog */}
            <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Lasă o recenzie</DialogTitle>
                        <DialogDescription>
                            Evaluează experiența ta cu {isBuyer ? order?.seller_username : order?.buyer_username}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <p className="text-sm font-medium mb-2">Rating</p>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setReviewRating(star)}
                                        className="p-1 hover:scale-110 transition-transform"
                                    >
                                        <Star
                                            className={`w-8 h-8 ${
                                                star <= reviewRating
                                                    ? 'text-yellow-500 fill-yellow-500'
                                                    : 'text-gray-300'
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-medium mb-2">Comentariu (opțional)</p>
                            <Textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                placeholder="Scrie despre experiența ta..."
                                rows={4}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReviewDialog(false)}>
                            Anulează
                        </Button>
                        <Button onClick={handleSubmitReview} disabled={submittingReview}>
                            {submittingReview ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Star className="w-4 h-4 mr-2" />
                            )}
                            Trimite recenzia
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Shipping Dialog */}
            <Dialog open={shippingDialog} onOpenChange={setShippingDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmă expedierea</DialogTitle>
                        <DialogDescription>
                            Selectează firma de curierat și introdu numărul AWB
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <p className="text-sm font-medium mb-2">Firma de curierat</p>
                            <select
                                value={courierCompany}
                                onChange={(e) => setCourierCompany(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
                            >
                                <option value="">Selectează curierul</option>
                                {courierCompanies.map((courier) => (
                                    <option key={courier.value} value={courier.value}>
                                        {courier.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <p className="text-sm font-medium mb-2">Număr AWB</p>
                            <input
                                type="text"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Ex: 2093847562"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-700">
                                <strong>💡 Sfat:</strong> Cumpărătorul va primi un link direct pentru urmărirea coletului.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShippingDialog(false)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleConfirmShipment} 
                            disabled={confirmingShipment || !courierCompany || !trackingNumber.trim()}
                            className="bg-primary hover:bg-primary/90"
                        >
                            {confirmingShipment ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Truck className="w-4 h-4 mr-2" />
                            )}
                            Confirmă expedierea
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dispute Dialog */}
            <Dialog open={disputeDialog} onOpenChange={setDisputeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deschide dispută</DialogTitle>
                        <DialogDescription>
                            Descrie problema întâmpinată cu această comandă
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-700">
                                <strong>Atenție:</strong> Deschiderea unei dispute va bloca fondurile până la rezolvare.
                                Echipa noastră va analiza disputa și va lua o decizie.
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-medium mb-2">Motivul disputei</p>
                            <Textarea
                                value={disputeReason}
                                onChange={(e) => setDisputeReason(e.target.value)}
                                placeholder="Descrie în detaliu problema întâmpinată (minim 20 caractere)..."
                                rows={5}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {disputeReason.length}/20 caractere minim
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDisputeDialog(false)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleSubmitDispute} 
                            disabled={submittingDispute || disputeReason.trim().length < 20}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {submittingDispute ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <AlertTriangle className="w-4 h-4 mr-2" />
                            )}
                            Deschide disputa
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Listing Dialog */}
            <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Șterge anunțul</DialogTitle>
                        <DialogDescription>
                            Ești sigur că vrei să ștergi acest anunț? Această acțiune este ireversibilă.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-sm text-red-700">
                                <strong>Atenție:</strong> Ștergerea anunțului va elimina definitiv toate informațiile asociate.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog(false)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleDeleteListing} 
                            disabled={deletingListing}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deletingListing ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Șterge anunțul
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrderPage;
